import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axiosApexInstance from '../auth/axiosApexInstance';
import {MISSING_TOKEN_ERROR_MSG} from "../Constants";
import {useUser} from "./UserContext";

// DTO type for config items
export interface AppConfigKeyDTO {
    id: number;
    configKey: string;
    category: string;
    valueType: string;
    dfltValue: string;
    allowGroupCustom: number;
    allowDeviceCustom: number;
    allowUserCustom: number;
    uiShow: number;
    uiDispOrder: number | null;
    uiGroup: string;
    uiSubgroup: string;
    uiTitle: string;
    uiDescription: string;
    uiOptionsHelp: string;
    modifiedAt: string;
    delFlag: number;
    configValue: string;
}

// Context type for config
export interface AppConfigContextType {
    configMap: { [key: string]: AppConfigKeyDTO };
    originalConfigMap: { [key: string]: AppConfigKeyDTO };
    resolvedConfig: { [key: string]: string };
    saveConfig: () => Promise<void>;
    setConfigValue: (configKey: string, newValue: string) => void;
    fetchConfig: () => Promise<void>;
    addConfigItem: (newItem: AppConfigKeyDTO) => Promise<void>; // Added for adding items
    updateConfigItem: (updatedItem: AppConfigKeyDTO) => Promise<void>; // Added for updating items
    deleteConfigItem: (id: number) => Promise<void>; // Added for deleting items
}

// Create context
const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

export const AppConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, accessToken } = useUser();
    const [configMap, setConfigMap] = useState<{ [key: string]: AppConfigKeyDTO }>({});
    const [originalConfigMap, setOriginalConfigMap] = useState<{ [key: string]: AppConfigKeyDTO }>({});

    // Fetch configuration from server
    const fetchConfig = async () => {
        if (!user || !accessToken) return;
        try {
            const userEmail = "siva@korlakunta.com"; // Get this from the current user context or auth state
            const userId = 1; // Get this from the current user context or auth state
            const deviceId = "device123"; // Get this from device info or context
            const groupNames = ["group1", "group2"]; // Specify which groups to resolve config for

            const response = await axiosApexInstance.post(
                '/task/config/resolve',
                groupNames, // Send the group names as the request body
                {
                    params: {
                        userEmail, // Send userEmail, userId, and deviceId as query parameters
                        userId,
                        deviceId
                    }
                }
            );

            setConfigMap(response.data); // Now, `response.data` contains full AppConfigKeyDTO objects with configValue
        } catch (error: any) {
            // Suppress error log if it's due to missing access token (pre-login case)
            if (error?.message === MISSING_TOKEN_ERROR_MSG ) {
                return;
            }

            console.error('Failed to fetch app config', error);
        }

    };

    // Save configuration to server
    const saveConfig = async () => {
        try {
            const changes = Object.keys(configMap).filter(
                key => configMap[key].dfltValue !== originalConfigMap[key]?.dfltValue
            );

            if (changes.length === 0) {
                console.log('No changes to save.');
                return;
            }

            const payload = changes.map(key => ({
                configKey: key,
                configValue: configMap[key].dfltValue,
            }));

            await axiosApexInstance.post('/task/config/save', payload, {
                headers: { 'Content-Type': 'application/json' },
            });

            setOriginalConfigMap({ ...configMap });
            console.log('Config saved successfully');
        } catch (error) {
            console.error('Failed to save config', error);
        }
    };

    // Set configuration value
    const setConfigValue = (key: string, value: string) => {
        setConfigMap(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                dfltValue: value,
            },
        }));
    };

    // Add a new config item
    const addConfigItem = async (newItem: AppConfigKeyDTO) => {
        try {
            await axiosApexInstance.post('/task/config/add', newItem);
            console.log(`Config item ${newItem.configKey} added`);
            fetchConfig(); // Refresh config after adding
        } catch (error) {
            console.error('Failed to add config item', error);
        }
    };

    // Update an existing config item
    const updateConfigItem = async (updatedItem: AppConfigKeyDTO) => {
        try {
            await axiosApexInstance.put('/task/config/update', updatedItem);
            console.log(`Config item ${updatedItem.configKey} updated`);
            fetchConfig(); // Refresh config after updating
        } catch (error) {
            console.error('Failed to update config item', error);
        }
    };

    // Delete a config item
    const deleteConfigItem = async (id: number) => {
        try {
            await axiosApexInstance.delete(`/task/config/delete/${id}`);
            console.log(`Config item with id ${id} deleted`);
            fetchConfig(); // Refresh config after deletion
        } catch (error) {
            console.error('Failed to delete config item', error);
        }
    };

    // Resolved config
    const resolvedConfig = useMemo(() => {
        const result: { [key: string]: string } = {};
        Object.keys(configMap).forEach(key => {
            result[key] = configMap[key].dfltValue;
        });
        return result;
    }, [configMap]);

    // Fetch config once on startup
    useEffect(() => {
        fetchConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, accessToken]);

    return (
        <AppConfigContext.Provider
            value={{
                configMap,
                originalConfigMap,
                resolvedConfig,
                saveConfig,
                setConfigValue,
                fetchConfig,
                addConfigItem,
                updateConfigItem,
                deleteConfigItem,
            }}
        >
            {children}
        </AppConfigContext.Provider>
    );
};

export const useAppConfig = () => {
    const context = useContext(AppConfigContext);
    if (!context) {
        throw new Error('useAppConfig must be used within an AppConfigProvider');
    }
    return context;
};
